import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";
import { toast } from "sonner";
import { Check, Compass } from "lucide-react";

const PILLAR_LABELS = { DREAM: "Dream", PRODUCT_PROOF: "Product Proof", VALUE: "Value", AFFORDABILITY: "Affordability", COMMUNITY: "Community", TRUST: "Trust", FAQ: "FAQ" };

export default function Onboarding() {
  const nav = useNavigate();
  const [items, setItems] = useState([]);

  useEffect(() => {
    api.onboardingSuggestions().then((d) => {
      setItems(d.suggestions.map((s) => ({ ...s, selected: true, date: "", status: "Completed" })));
    });
  }, []);

  const update = (i, patch) => setItems((arr) => arr.map((it, idx) => (idx === i ? { ...it, ...patch } : it)));

  const save = async () => {
    const chosen = items.filter((i) => i.selected).map((i) => ({
      action_type: i.action_type, platform: i.platform, audience: i.audience,
      pillar: i.pillar, theme: i.theme, notes: i.notes,
      status: i.status, date: i.date || null,
    }));
    if (chosen.length === 0) { toast.error("Select at least one item, or skip."); return; }
    await api.onboardingConfirm(chosen);
    toast.success(`Saved ${chosen.length} historical activities`);
    nav("/");
  };

  return (
    <div className="space-y-6 max-w-4xl">
      <div>
        <div className="flex items-center gap-2 eyebrow text-[#D4AF37]"><Compass className="w-4 h-4" /> First-run setup</div>
        <h1 className="font-display text-3xl sm:text-4xl mt-1">What have we already done?</h1>
        <p className="text-[#94A3B8] mt-2 max-w-2xl">Thailand marketing didn't start today. Review the activities below, adjust or add dates, and confirm the ones that really happened. Nothing is saved as completed until you confirm — no dates are invented.</p>
      </div>

      <div className="space-y-3">
        {items.map((it, i) => (
          <div key={i} className={`card-lux p-4 ${it.selected ? "" : "opacity-50"}`} data-testid={`seed-item-${i}`}>
            <div className="flex items-start gap-3">
              <button onClick={() => update(i, { selected: !it.selected })}
                className={`mt-1 w-5 h-5 rounded flex items-center justify-center border ${it.selected ? "bg-[#D4AF37] border-[#D4AF37]" : "border-[#2A3042]"}`}>
                {it.selected && <Check className="w-3.5 h-3.5 text-black" />}
              </button>
              <div className="flex-1">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className="text-[#F8FAFC] font-medium">{it.action_type}</span>
                  <span className="text-xs px-2 py-0.5 rounded bg-[#222736] text-[#D4AF37]">{PILLAR_LABELS[it.pillar] || it.pillar}</span>
                  <span className="text-xs text-[#64748B]">{it.audience} · {it.platform}</span>
                </div>
                <div className="text-sm text-[#94A3B8] mt-1">{it.notes}</div>
                <div className="flex items-center gap-3 mt-3 flex-wrap">
                  <div>
                    <label className="text-[10px] text-[#64748B] uppercase tracking-wider block">Date (optional)</label>
                    <input type="date" value={it.date} onChange={(e) => update(i, { date: e.target.value })}
                      className="mt-1 px-2 py-1.5 rounded bg-[#0A0C10] border border-[#2A3042] text-sm text-[#F8FAFC] outline-none focus:border-[#D4AF37]" />
                  </div>
                  <div>
                    <label className="text-[10px] text-[#64748B] uppercase tracking-wider block">Status</label>
                    <select value={it.status} onChange={(e) => update(i, { status: e.target.value })}
                      className="mt-1 px-2 py-1.5 rounded bg-[#0A0C10] border border-[#2A3042] text-sm text-[#F8FAFC] outline-none focus:border-[#D4AF37]">
                      <option>Completed</option><option>Planned</option><option>Ready</option>
                    </select>
                  </div>
                </div>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-3">
        <button data-testid="onboarding-save" onClick={save} className="gold-btn px-6 py-3 rounded-lg">Confirm & save history</button>
        <button onClick={() => nav("/")} className="px-6 py-3 rounded-lg border border-[#2A3042] text-[#94A3B8]">Skip for now</button>
      </div>
    </div>
  );
}
